import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatedBackground } from '@/components/AnimatedBackground';
import { ArrowRight, Calendar, MoonStar, Orbit, Sparkles } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { subscribeDashboardMeta, loadDashboardMeta } from '@/lib/dashboardStore';
import { executeAndVerifyRecaptcha } from '@/lib/recaptcha';
import { ensureRecaptchaEnterpriseScript } from '@/lib/recaptchaConfig';
import { addDoc, collection } from 'firebase/firestore';
// Do not import db at module scope to avoid circular import issues
const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (sitekey: string, options: { action: string }) => Promise<string>;
      };
      render?: (elementId: string, options: { sitekey: string; theme: 'light' | 'dark' }) => void;
      getResponse?: (widgetId?: number) => string;
      reset?: (widgetId?: number) => void;
    };
  }
}

const Index = () => {
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();

  const submitEmail = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    const addr = (email || '').trim().toLowerCase();
    if (!addr || !addr.includes('@')) return toast.error('Please enter a valid email');

    // Get and verify reCAPTCHA token
    let recaptchaToken = '';
    try {
      const result = await executeAndVerifyRecaptcha('SUBMIT', true, 0.5);
      recaptchaToken = result.token;
      if (!result.isValid) {
        toast.error('Verification failed - please try again');
        return;
      }
    } catch (err) {
      console.warn('reCAPTCHA verification error:', err);
      toast.error('Verification error - please try again');
      return;
    }

    try {
      const db = await getDb();
      if (db) {
        await addDoc(collection(db, 'earlyAccess'), {
          email: addr,
          createdAt: new Date().toISOString(),
          recaptchaToken,
        });
      } else {
        // fallback to localStorage
        try {
          const existing = JSON.parse(window.localStorage.getItem('earlyAccessEmails') || '[]');
          existing.push({ email: addr, createdAt: new Date().toISOString(), recaptchaToken });
          window.localStorage.setItem('earlyAccessEmails', JSON.stringify(existing));
        } catch {}
      }

      // Special-case approved tester
      if (addr === 'pro1polaris@gmail.com') {
        toast.success('Access granted - redirecting to sign-in');
        navigate('/beta');
        return;
      }

      setEmail('');
      toast.success('Thanks - we will notify you');
    } catch (err) {
      console.error('Notify submit failed', err);
      toast.error('Failed to save email');
    }
  }, [email, navigate]);

  useEffect(() => {
    const script = ensureRecaptchaEnterpriseScript();

    return () => {
      if (script?.dataset.recaptchaManaged === '1') {
        script.remove();
      }
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    let unsubscribe: (() => void) | undefined;

    const applyMeta = (meta: { comingSoonEnabled?: boolean } | null) => {
      if (!isActive) return;
      setComingSoonEnabled(Boolean(meta?.comingSoonEnabled));
    };


    try {
      unsubscribe = subscribeDashboardMeta((meta) => {
        applyMeta(meta);
        if (isActive) setMetaLoaded(true);
      });
    } catch {
      // noop: fall through to load
    }

    if (!unsubscribe) {
      (async () => {
        try {
          const meta = await loadDashboardMeta();
          applyMeta(meta);
        } catch {
          // ignore load errors
        } finally {
          if (isActive) setMetaLoaded(true);
        }
      })();
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, []);

  if (comingSoonEnabled && metaLoaded) {
    return (
      <div className="min-h-screen bg-background nebula-bg relative overflow-hidden flex items-center justify-center px-4">
        <AnimatedBackground />
        <div className="w-full max-w-lg text-center space-y-8 relative z-10 animate-fade-in">
          <div className="space-y-2">
            <h1 className="text-4xl font-display font-bold">Coming Soon</h1>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              We're preparing the new dashboard. Access is limited — sign in to continue.
            </p>
          </div>

          <div className="space-y-4">
            <form onSubmit={submitEmail} className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur p-6 grid gap-4">
              <p className="text-sm text-muted-foreground">Get notified when we launch (enter your email).</p>
              <input
                type="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="you@example.com"
                className="w-full px-3 py-2 rounded-md bg-card/80 border border-border/50"
                required
              />
              <div className="flex gap-3">
                <button type="submit" className="flex-1 px-4 py-2 rounded-md bg-primary text-white">Get notified</button>
                <a href="/beta" className="px-4 py-2 rounded-md border border-border/50">Sign in</a>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background nebula-bg relative overflow-hidden flex items-center justify-center px-4">
      <AnimatedBackground />
      <div className="w-full max-w-6xl mx-auto py-14 md:py-20 relative z-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_0.9fr] lg:gap-14 items-start">
          <section className="px-2 md:px-0 animate-fade-in">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              THIRTEEN Galactic Calendar
            </div>

            <h1 className="mt-6 max-w-4xl text-5xl font-display font-bold leading-tight md:text-6xl lg:text-7xl">
              A cosmic homepage for the <span className="text-cosmic-gradient">13-month time system</span>.
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-muted-foreground md:text-xl">
              THIRTEEN overlays lunar phases, solar cycles, and real-world schedules in one calendar. It is built for people
              who want a calmer rhythm than the standard grid without losing sync with actual time.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/beta"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 font-medium text-primary-foreground cosmic-glow-sm transition-transform duration-300 hover:-translate-y-0.5"
              >
                Enter the Calendar
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/plans"
                className="inline-flex items-center justify-center rounded-xl border border-border/60 bg-card/60 px-6 py-3 font-medium backdrop-blur-sm transition-colors hover:border-primary/40 hover:bg-card"
              >
                View Plans
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-border/60 bg-card/55 p-5 backdrop-blur-sm">
                <div className="text-3xl font-display text-cosmic-gradient">13</div>
                <div className="mt-2 text-sm uppercase tracking-[0.25em] text-muted-foreground">Months</div>
                <p className="mt-3 text-sm text-muted-foreground">A full 13-month rhythm with threshold days and Gregorian-safe timestamps.</p>
              </div>
              <div className="rounded-2xl border border-border/60 bg-card/55 p-5 backdrop-blur-sm">
                <div className="text-3xl font-display text-cosmic-gradient">Moon</div>
                <div className="mt-2 text-sm uppercase tracking-[0.25em] text-muted-foreground">Aligned</div>
                <p className="mt-3 text-sm text-muted-foreground">Real lunar phase tracking instead of symbolic approximations or fake cycle math.</p>
              </div>
              <div className="rounded-2xl border border-border/60 bg-card/55 p-5 backdrop-blur-sm">
                <div className="text-3xl font-display text-cosmic-gradient">Sync</div>
                <div className="mt-2 text-sm uppercase tracking-[0.25em] text-muted-foreground">Ready</div>
                <p className="mt-3 text-sm text-muted-foreground">Built to export and integrate with real calendars, reminders, and future device sync.</p>
              </div>
            </div>
          </section>

          <aside className="px-2 md:px-0 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="overflow-hidden rounded-[28px] border border-border/60 bg-card/65 backdrop-blur-md shadow-2xl shadow-primary/10">
              <div className="border-b border-border/50 px-6 py-5">
                <p className="text-xs uppercase tracking-[0.35em] text-muted-foreground">Live Preview</p>
                <h2 className="mt-2 text-2xl font-display">Track time in layers, not boxes.</h2>
              </div>

              <div className="space-y-4 px-6 py-6">
                <div className="rounded-2xl border border-accent/20 bg-background/50 p-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="mt-0.5 h-5 w-5 text-accent" />
                    <div>
                      <div className="font-medium">13-Month Calendar</div>
                      <p className="mt-1 text-sm text-muted-foreground">See the galactic month structure without losing your real-world dates.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-primary/20 bg-background/50 p-4">
                  <div className="flex items-start gap-3">
                    <MoonStar className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <div className="font-medium">Lunar Truth Layer</div>
                      <p className="mt-1 text-sm text-muted-foreground">Moon phases and cycle markers are based on actual astronomical timing.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-secondary/80 bg-background/50 p-4">
                  <div className="flex items-start gap-3">
                    <Orbit className="mt-0.5 h-5 w-5 text-cosmic-gradient" />
                    <div>
                      <div className="font-medium">Cycle Dashboard</div>
                      <p className="mt-1 text-sm text-muted-foreground">Layers for events, reflections, sync feeds, and future shared ritual planning.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-border/50 bg-background/40 p-4">
                  <p className="text-sm text-muted-foreground">Ready to test the actual app?</p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <Link to="/beta" className="inline-flex flex-1 items-center justify-center rounded-xl bg-primary px-4 py-3 font-medium text-primary-foreground">
                      Open Beta
                    </Link>
                    <Link to="/dashboard" className="inline-flex flex-1 items-center justify-center rounded-xl border border-border/60 px-4 py-3 font-medium">
                      Open Dashboard
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Index;
